import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit2, Plus, Save, ArrowUp, ArrowDown, Trash2, ChevronDown, ChevronUp, Info, X, Factory } from 'lucide-react';
import { CONDITION_OPERATORS, MACHINE_MAN_TYPES } from './constants';

/**
 * A stage is only a production stage if the floor can staff it.
 *
 * The pipeline picked on a job card and the stage registry the Machines screen
 * links to are two lists. A stage that exists here but has no registry row, or
 * has one with no machine against it, can be numbered on a job card and then
 * never worked — which shows up three days later rather than here.
 */
export default function StageRulesEditor({
  rules, editMode, allFieldKeys, expandedRules, setExpandedRules,
  onStartEdit, onCancel, onSave, onAdd, onRemove, onMove, onUpdate,
  onAddCondition, onRemoveCondition, onUpdateCondition, saving,
  registryStages = [],
}) {
  const toggleExpand = (idx) => {
    setExpandedRules(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const registryByKey = new Map(registryStages.filter(s => s.stage_key).map(s => [s.stage_key, s]));
  const registryByName = new Map(registryStages.map(s => [s.name, s]));
  // rows seeded before stage_key existed carry a name and nothing else
  const registryRow = (rule) =>
    registryByKey.get(rule.stage_key) || registryByName.get(rule.stage_name) || null;

  const unstaffed = registryStages.length
    ? rules.filter(rule => (registryRow(rule)?.linked_machine_count || 0) === 0)
    : [];

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-slate-600 font-medium">{rules.length} production stages</p>
          <p className="text-xs text-slate-400">Configure which stages appear in the production pipeline and when.</p>
        </div>
        {!editMode ? (
          <Button size="sm" variant="outline" onClick={onStartEdit} data-testid="edit-stages-btn">
            <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit Stages
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onAdd} data-testid="add-stage-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Stage
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button size="sm" onClick={onSave} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-stages-btn">
              <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {unstaffed.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-600"
          data-testid="unstaffed-warning">
          <Factory className="h-4 w-4 shrink-0 mt-0.5 text-slate-400" />
          <p>
            {unstaffed.length} of these {rules.length} processes have no machine linked yet, so nobody
            can be put against them on the job card. Link machines under{' '}
            <span className="font-medium">Machines</span> — the stage itself now exists on the floor.
          </p>
        </div>
      )}

      <div className="h-[400px] overflow-y-auto border rounded-lg">
        <div className="space-y-2 p-2">
          {rules.map((rule, idx) => {
            const hasConditions = (rule.conditions || []).length > 0;
            const isExpanded = expandedRules[idx];
            
            return (
              <div key={rule._key || rule.id || idx} className="rounded-lg border bg-white overflow-hidden" data-testid={`stage-row-${idx}`}>
                <div className="p-3">
                  {editMode ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-0.5">
                          <button onClick={() => onMove(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => onMove(idx, 1)} disabled={idx === rules.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <span className="text-xs text-slate-400 w-6">{idx + 1}</span>
                        <div className="flex-1 flex flex-col gap-2 md:grid md:grid-cols-12 md:items-center">
                          <div className="col-span-3">
                            <Input
                              value={rule.stage_name}
                              onChange={(e) => onUpdate(idx, 'stage_name', e.target.value)}
                              placeholder="Stage Name"
                              className="text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              value={rule.stage_key}
                              onChange={(e) => onUpdate(idx, 'stage_key', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                              placeholder="stage_key"
                              className="text-xs font-mono"
                            />
                          </div>
                          <div className="col-span-3">
                            <Select value={rule.machine_man_type || ''} onValueChange={(v) => onUpdate(idx, 'machine_man_type', v || null)}>
                              <SelectTrigger className="text-xs"><SelectValue placeholder="Operator Type" /></SelectTrigger>
                              <SelectContent>
                                {MACHINE_MAN_TYPES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-3 flex items-center gap-2">
                            <label className="flex items-center gap-1.5 cursor-pointer whitespace-nowrap" title="Allow sending to external vendor at this stage">
                              <input
                                type="checkbox"
                                checked={rule.supports_external_vendor || false}
                                onChange={(e) => onUpdate(idx, 'supports_external_vendor', e.target.checked)}
                                className="rounded border-slate-300"
                              />
                              <span className="text-[10px] text-slate-500">Ext. Vendor</span>
                            </label>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => toggleExpand(idx)}
                              className="text-xs"
                            >
                              {(rule.conditions || []).length} conditions
                              {isExpanded ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                            </Button>
                          </div>
                        </div>
                        <button onClick={() => onRemove(idx)} className="text-red-400 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {isExpanded && (
                        <div className="ml-10 mt-3 p-3 bg-slate-50 rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-slate-600">
                              <Info className="h-3 w-3 inline mr-1" />
                              Include this stage when:
                            </p>
                            <Button size="sm" variant="ghost" onClick={() => onAddCondition(idx)} className="text-xs h-7">
                              <Plus className="h-3 w-3 mr-1" /> Add Condition
                            </Button>
                          </div>
                          
                          {(rule.conditions || []).length === 0 && (
                            <p className="text-xs text-slate-400 italic">No conditions — stage always included</p>
                          )}
                          
                          {(rule.conditions || []).map((cond, condIdx) => (
                            <div key={condIdx} className="flex items-center gap-2 p-2 bg-white rounded border">
                              <Select 
                                value={cond.field} 
                                onValueChange={(v) => onUpdateCondition(idx, condIdx, 'field', v)}
                              >
                                <SelectTrigger className="w-[140px] text-xs">
                                  <SelectValue placeholder="Select field" />
                                </SelectTrigger>
                                <SelectContent>
                                  {Array.from(allFieldKeys).map(k => (
                                    <SelectItem key={k} value={k}>{k}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              
                              <Select 
                                value={cond.operator} 
                                onValueChange={(v) => onUpdateCondition(idx, condIdx, 'operator', v)}
                              >
                                <SelectTrigger className="w-[130px] text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {CONDITION_OPERATORS.map(op => (
                                    <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              
                              {CONDITION_OPERATORS.find(o => o.value === cond.operator)?.needsValue && (
                                <Input
                                  value={Array.isArray(cond.value) ? cond.value.join(', ') : cond.value || ''}
                                  onChange={(e) => onUpdateCondition(idx, condIdx, 'value', e.target.value)}
                                  placeholder={CONDITION_OPERATORS.find(o => o.value === cond.operator)?.isArray ? "value1, value2" : "value"}
                                  className="flex-1 text-xs"
                                />
                              )}
                              
                              <label className="flex items-center gap-1 text-[10px] whitespace-nowrap" title="Include stage even if field is empty">
                                <input
                                  type="checkbox"
                                  checked={cond.include_when_empty || false}
                                  onChange={(e) => onUpdateCondition(idx, condIdx, 'include_when_empty', e.target.checked)}
                                  className="rounded"
                                />
                                If empty
                              </label>
                              
                              <button onClick={() => onRemoveCondition(idx, condIdx)} className="text-red-400 hover:text-red-600">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 w-6">{idx + 1}</span>
                      <span className="text-sm font-medium text-slate-700">{rule.stage_name}</span>
                      <span className="text-[10px] text-slate-400 font-mono">{rule.stage_key}</span>
                      {rule.machine_man_type && (
                        <Badge variant="outline" className="text-[10px]">
                          {MACHINE_MAN_TYPES.find(m => m.value === rule.machine_man_type)?.label || rule.machine_man_type}
                        </Badge>
                      )}
                      {hasConditions ? (
                        <Badge className="text-[10px] bg-amber-50 text-amber-700">
                          {rule.conditions.length} condition{rule.conditions.length > 1 ? 's' : ''}
                        </Badge>
                      ) : (
                        <Badge className="text-[10px] bg-green-50 text-green-700">Always</Badge>
                      )}
                      {rule.supports_external_vendor && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-600 border-amber-200">
                          External Vendor
                        </Badge>
                      )}
                      {registryStages.length > 0 && (() => {
                        const row = registryRow(rule);
                        if (!row) {
                          return (
                            <Badge className="text-[10px] bg-red-50 text-red-700" data-testid={`registry-${idx}`}>
                              not a production stage
                            </Badge>
                          );
                        }
                        const machines = row.linked_machine_count || 0;
                        return (
                          <span
                            className={`text-[10px] ${machines === 0 ? 'text-slate-400' : 'text-slate-600'}`}
                            data-testid={`registry-${idx}`}>
                            {row.category} · {machines === 0 ? 'no machine' : `${machines} machine${machines > 1 ? 's' : ''}`}
                          </span>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
